import { useQueryClient } from '@tanstack/react-query';

import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';

import { useToaster } from '../../hooks/useToaster/useToaster.tsx';
import { UnsubscribedViewLayout } from '../../layout/UnsubscribedViewLayout.tsx';
import { SubscriptionModule } from '../../modules/subscription/SubscriptionModule.tsx';
import type { SubscriptionModuleMode } from '../../modules/subscription/types.ts';
import { selectIsOnboardingComplete } from '../../redux/features/selectors/accountSelectors.ts';
import { featuresSelectors } from '../../redux/features/selectors/featuresSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { useToolkitControllerGlobal } from '../hooks/useToolkitControllerGlobal.ts';

const { selectShouldAllowTrialWithoutPayment } = featuresSelectors;

const getSubscriptionModuleMode = (params: {
  allowTrialWithoutPayment: boolean;
  isNewUser: boolean;
}): SubscriptionModuleMode => {
  const { allowTrialWithoutPayment, isNewUser } = params;
  if (allowTrialWithoutPayment) return 'add-payment-method';
  return isNewUser ? 'legacy-new-user-trial' : 'subscription-expired';
};

export const SubscribeRouteElement = () => {
  const { subscription } = useToolkitControllerGlobal();
  const toast = useToaster();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isOnboardingComplete = useAppSelector(selectIsOnboardingComplete);

  const allowTrialWithoutPayment = useAppSelector((state) =>
    selectShouldAllowTrialWithoutPayment(state, subscription),
  );

  const isNewUser = subscription.previousSubscriptions.length === 0;

  const [title, setTitle] = useState(isNewUser ? 'Select a plan' : 'Subscription Expired');
  const [description, setDescription] = useState(
    isNewUser
      ? undefined
      : 'Your subscription is currently inactive. Please activate a new subscription.',
  );

  if (!isOnboardingComplete) return <Navigate to="/onboarding" />;

  if (!allowTrialWithoutPayment && subscription.hasActiveSubscription) {
    return <Navigate to="/orders" />;
  }

  const mode = getSubscriptionModuleMode({ allowTrialWithoutPayment, isNewUser });

  return (
    <UnsubscribedViewLayout title={title} description={description}>
      <SubscriptionModule
        mode={mode}
        onPlanSelect={() => {
          setTitle('Complete checkout');
          setDescription(undefined);
        }}
        onFinish={async () => {
          toast({
            id: 'onboarding-subscription-success',
            type: 'success',
            title: 'Subscription successful!',
            body: 'Thank you for subscribing to Appraiser Toolkit!',
          });

          // Now that the user is subscribed, invalidate the cache so
          // that we don't get redirected to /subscribe again.
          await queryClient.invalidateQueries({
            queryKey: ['api', 'v2', 'stripe', 'subscriptionStatus'],
          });

          navigate('/orders');
        }}
        // NOTE: This is currently not used as we don't accept payment methods that require
        // redirection: https://docs.stripe.com/payments/payment-methods/overview#bank-redirects
        redirectURL="https://toolkit.aloftappraisal.com"
      />
    </UnsubscribedViewLayout>
  );
};
